import React, { useCallback, useEffect, useState } from "react";
import "./App.css";

import Header from "../components/layout/Header";
import Sidebar from "../components/layout/Sidebar";
import Workspace from "../components/layout/Workspace";
import RightPanel from "../components/layout/RightPanel";
import Footer from "../components/layout/Footer";
import Drawer from "../components/common/Drawer";
import ContactChat from "../components/support/ContactChat";
import InfoSection from "../components/marketing/InfoSection";

import { useTheme } from "../hooks/useTheme";
import { useDeviceSelection } from "../hooks/useDeviceSelection";
import { usePreviewSessions } from "../hooks/usePreviewSessions";
import { useLocalStorage } from "../hooks/useLocalStorage";
import { useMediaQuery } from "../hooks/useMediaQuery";

import { normalizeUrl } from "../utils/urlUtils";
import { STORAGE_KEYS } from "../constants/storageKeys";
import { LAYOUT_MODES, MAX_HISTORY_ITEMS, SORT_MODES } from "../constants/previewConfig";
import { isValidLayoutMode, isValidZoom, sanitizeLayoutMode, sanitizeZoom } from "../utils/sessionUtils";

const isHistoryArray = (value) =>
  Array.isArray(value) && value.every((item) => item && typeof item.url === "string" && typeof item.timestamp === "string");
// The last URL is fed straight into every iframe `src`, so a tampered value
// must pass the same check as the address bar (http/https + real host).
const isSafeLastUrl = (value) => value === "" || (typeof value === "string" && normalizeUrl(value).valid);

// Browsers start reading a blob: download asynchronously after click(), so
// revoking its URL in the same tick can cancel the download.
const DOWNLOAD_URL_REVOKE_DELAY_MS = 1000;

function App() {
  const theme = useTheme();
  const deviceSelection = useDeviceSelection();
  const sessions = usePreviewSessions();

  const [finalUrl, setFinalUrl] = useLocalStorage(STORAGE_KEYS.LAST_URL, "", isSafeLastUrl);
  const [url, setUrl] = useState(() => finalUrl);
  const [history, setHistory] = useLocalStorage(STORAGE_KEYS.HISTORY, [], isHistoryArray);
  const [layoutMode, setLayoutMode] = useLocalStorage(STORAGE_KEYS.LAYOUT_MODE, LAYOUT_MODES.GRID, isValidLayoutMode);
  const [globalZoom, setGlobalZoom] = useLocalStorage(STORAGE_KEYS.GLOBAL_ZOOM, "fit", isValidZoom);
  const [sortMode, setSortMode] = useState(SORT_MODES.DEFAULT);
  const [globalReloadToken, setGlobalReloadToken] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [rightPanelOpen, setRightPanelOpen] = useState(false);
  const [importError, setImportError] = useState(null);

  const isDesktop = useMediaQuery("(min-width: 960px)");

  const loadUrl = useCallback(
    (normalizedUrl) => {
      // Re-submitting the URL that's already showing won't change any iframe
      // `src`, so explicitly reload every preview instead of doing nothing.
      if (normalizedUrl === finalUrl) setGlobalReloadToken((prev) => prev + 1);
      setFinalUrl(normalizedUrl);
      setUrl(normalizedUrl);
      setHistory((prev) => {
        const withoutDuplicate = prev.filter((item) => item.url !== normalizedUrl);
        return [{ url: normalizedUrl, timestamp: new Date().toISOString() }, ...withoutDuplicate].slice(
          0,
          MAX_HISTORY_ITEMS
        );
      });
    },
    [finalUrl, setFinalUrl, setHistory]
  );

  const handleSelectHistory = useCallback(
    (historyUrl) => {
      const result = normalizeUrl(historyUrl);
      if (result.valid) loadUrl(result.url);
    },
    [loadUrl]
  );

  const handleDeleteHistoryItem = useCallback(
    (historyUrl) => {
      setHistory((prev) => prev.filter((item) => item.url !== historyUrl));
    },
    [setHistory]
  );

  const handleHelp = useCallback(() => {
    document.getElementById("info-section")?.scrollIntoView({ behavior: "smooth" });
  }, []);

  const sessionSnapshot = useCallback(
    () => ({
      url: finalUrl,
      selectedDeviceIds: deviceSelection.selectedDeviceIds,
      orientationByDevice: deviceSelection.orientationByDevice,
      globalZoom,
      layoutMode,
    }),
    [finalUrl, deviceSelection.selectedDeviceIds, deviceSelection.orientationByDevice, globalZoom, layoutMode]
  );

  const handleSaveSession = useCallback(
    (name) => sessions.saveSession(name, sessionSnapshot()),
    [sessions, sessionSnapshot]
  );

  const handleLoadSession = useCallback(
    (session) => {
      // Sessions are validated on read/import, but re-check here too: this is
      // the last stop before the URL becomes an iframe `src`.
      const result = normalizeUrl(session.url);
      if (!result.valid) return;
      setUrl(result.url);
      setFinalUrl(result.url);
      deviceSelection.applySelection({
        selectedDeviceIds: session.selectedDeviceIds,
        orientationByDevice: session.orientationByDevice,
      });
      setGlobalZoom(sanitizeZoom(session.globalZoom));
      setLayoutMode(sanitizeLayoutMode(session.layoutMode));
      setRightPanelOpen(false);
    },
    [deviceSelection, setFinalUrl, setGlobalZoom, setLayoutMode]
  );

  const handleExportSession = useCallback(
    (id) => {
      const json = sessions.exportSession(id);
      if (!json) return;
      const session = sessions.sessions.find((s) => s.id === id);
      const blob = new Blob([json], { type: "application/json" });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = objectUrl;
      link.download = `${(session?.name || "session").replace(/\s+/g, "-").toLowerCase()}.json`;
      link.style.display = "none";
      document.body.appendChild(link);
      link.click();
      link.remove();
      setTimeout(() => URL.revokeObjectURL(objectUrl), DOWNLOAD_URL_REVOKE_DELAY_MS);
    },
    [sessions]
  );

  const handleImportSession = useCallback(
    (text) => {
      const result = sessions.importSessionFromJson(text);
      setImportError(result.valid ? null : result.error);
    },
    [sessions]
  );

  // Stable references: Drawer re-runs its focus management whenever onClose
  // changes, so inline arrows here used to reset focus on every App render.
  const closeSidebar = useCallback(() => setSidebarOpen(false), []);
  const closeRightPanel = useCallback(() => setRightPanelOpen(false), []);

  useEffect(() => {
    if (isDesktop) {
      setSidebarOpen(false);
      setRightPanelOpen(false);
    }
  }, [isDesktop]);

  const sessionProps = {
    sessions: sessions.sessions,
    canSave: Boolean(finalUrl),
    onSave: handleSaveSession,
    onLoad: handleLoadSession,
    onRename: sessions.renameSession,
    onDuplicate: sessions.duplicateSession,
    onDelete: sessions.deleteSession,
    onExport: handleExportSession,
    onImport: handleImportSession,
    importError,
  };

  return (
    <div className="app-page">
      <div className="app-shell">
        <Header
          url={url}
          setUrl={setUrl}
          onPreview={loadUrl}
          themeMode={theme.mode}
          onCycleTheme={theme.cycleMode}
          onToggleSidebar={() => setSidebarOpen(true)}
          onToggleRightPanel={() => setRightPanelOpen(true)}
          onHelp={handleHelp}
        />

        <div className="app-body">
          {isDesktop && (
            <aside className="app-sidebar">
              <Sidebar deviceSelection={deviceSelection} />
            </aside>
          )}

          <Workspace
            finalUrl={finalUrl}
            selectedDevices={deviceSelection.selectedDevices}
            orientationByDevice={deviceSelection.orientationByDevice}
            layoutMode={layoutMode}
            setLayoutMode={setLayoutMode}
            globalZoom={globalZoom}
            setGlobalZoom={setGlobalZoom}
            sortMode={sortMode}
            setSortMode={setSortMode}
            globalReloadToken={globalReloadToken}
            onReloadAll={() => setGlobalReloadToken((prev) => prev + 1)}
            onRotateAll={deviceSelection.rotateAll}
            onClearWorkspace={deviceSelection.clearSelection}
            onRotateDevice={deviceSelection.toggleOrientation}
            onRemoveDevice={deviceSelection.removeDevice}
          />

          {isDesktop && (
            <aside className="app-rightpanel">
              <RightPanel
                selectedDevices={deviceSelection.selectedDevices}
                orientationByDevice={deviceSelection.orientationByDevice}
                onRemoveDevice={deviceSelection.removeDevice}
                history={history}
                onSelectHistory={handleSelectHistory}
                onDeleteHistory={handleDeleteHistoryItem}
                onClearHistory={() => setHistory([])}
                sessionProps={sessionProps}
              />
            </aside>
          )}
        </div>

        {!isDesktop && sidebarOpen && (
          <Drawer side="left" title="Devices" onClose={closeSidebar}>
            <Sidebar deviceSelection={deviceSelection} />
          </Drawer>
        )}

        {!isDesktop && rightPanelOpen && (
          <Drawer side="right" title="History & sessions" onClose={closeRightPanel}>
            <RightPanel
              selectedDevices={deviceSelection.selectedDevices}
              orientationByDevice={deviceSelection.orientationByDevice}
              onRemoveDevice={deviceSelection.removeDevice}
              history={history}
              onSelectHistory={handleSelectHistory}
              onDeleteHistory={handleDeleteHistoryItem}
              onClearHistory={() => setHistory([])}
              sessionProps={sessionProps}
            />
          </Drawer>
        )}
      </div>

      <InfoSection />
      <Footer />
      <ContactChat />
    </div>
  );
}

export default App;
